import * as React from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Box, Grid } from '@mui/material';

import {
    ReminderPublisher,
    FactureAchat,
    FACTURES_ACHATS_FROM_PUBLISHER_REMINDER_DATASOURCE_NAME,
    CustomizerReducerType,
    setXIdSociete,
    useAchatsService,
    AchatsStateType,
    AchatsAppDispatch,
} from '@europrocurement/l2d-domain';
import { DataSource } from '@europrocurement/l2d-redux-utils';
import { useFullLayout } from '@europrocurement/flexy-components/components/templates/FullLayout/FullLayoutContext';
import { useLoadingStatus } from '@europrocurement/l2d-hooks';

import {
    EnhancedPanelSkeleton,
    HeaderSkeleton,
    SkeletonLoader,
} from '@europrocurement/flexy-components';
import { useMailContent } from './hooks/useMailContent';
import { useInvoicesFilter } from './hooks/useInvoicesFilter';
import MailPreview from './components/MailPreview';
import SupplierInfoCard from './components/SupplierInfoCard';
import InvoicesList from './components/InvoicesList';
import ReminderHistory from './components/ReminderHistory';

/**
 * Composant affichant les détails d'un rappel fournisseur
 *
 * Affiche une vue divisée avec :
 * - Gauche : Prévisualisation du mail et section de commentaire personnalisé
 * - Droite : Informations du fournisseur, liste des factures et boutons d'action
 */
const PublisherReminderDetails: React.FunctionComponent = function () {
    const { idPublisherReminder } = useParams<{ idPublisherReminder: string }>();
    const reminderId = idPublisherReminder;

    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch<AchatsAppDispatch>();
    const { setSidebarOpen } = useFullLayout();

    const { selectReminderPublisher } = useAchatsService();

    React.useEffect(() => {
        setSidebarOpen(false);
        return () => {
            setSidebarOpen(true);
        };
    }, [setSidebarOpen]);

    // État local
    const [attachmentPage, setAttachmentPage] = React.useState<number>(1);

    const attachmentCount = 1; // À remplacer par le compte réel

    // Sélecteurs Redux
    const reminderPublisherDataSource: DataSource<ReminderPublisher> = useSelector(
        (s: AchatsStateType) => s.achats.reminderPublisher.main,
    );

    const reminderPublisherFactureAchatsDataSource: DataSource<FactureAchat> = useSelector(
        (s: AchatsStateType) =>
            s.achats.factureachat[FACTURES_ACHATS_FROM_PUBLISHER_REMINDER_DATASOURCE_NAME],
    );

    const reminderPublisher = reminderPublisherDataSource.selected;
    const reminderPublisherFactureAchatCount =
        reminderPublisherFactureAchatsDataSource.pagination.total;

    // Hook pour le contenu du mail avec dépendances de déclenchement
    const {
        mailContent,
        refresh: refreshMailContent,
        isLoading: isLoadingMailContent,
    } = useMailContent(reminderId, {
        triggerDependencies: [reminderPublisher?.customMessage, reminderPublisherFactureAchatCount],
    });

    // Hook de filtrage des factures
    const { fetchInvoices } = useInvoicesFilter(
        reminderPublisherFactureAchatsDataSource,
        reminderPublisher?.id,
    );

    /**
     * Si le rappel a un idSociete,
     * alors le définir comme xIdSociete de l'utilisateur
     */
    React.useEffect(() => {
        if (reminderPublisher?.idSociete) {
            dispatch(
                setXIdSociete(reminderPublisher?.idSociete as CustomizerReducerType['xIdSociete']),
            );
        }
    }, [dispatch, reminderPublisher?.idSociete]);

    /**
     * Charger les données du rappel fournisseur au montage du composant
     */
    React.useEffect(() => {
        if (!reminderId) {
            enqueueSnackbar('Identifiant du rappel fournisseur manquant', { variant: 'error' });
            return;
        }

        if (reminderPublisher?.id !== reminderId) {
            dispatch(selectReminderPublisher({ id: reminderId }));
        }
    }, [dispatch, enqueueSnackbar, reminderId, reminderPublisher?.id, selectReminderPublisher]);

    /**
     * Vérifier si les données sont chargées et prêtes à être affichées
     */
    const loadGlobalData = React.useCallback(
        () => !!(reminderPublisher && reminderPublisherDataSource.selectedStatus !== 'loading'),
        [reminderPublisher, reminderPublisherDataSource.selectedStatus],
    );

    /**
     * Vérifier si les données du panneau de prévisualisation sont chargées et prêtes à être affichées
     */
    const loadPreviewPanelData = React.useCallback(
        () => !!(loadGlobalData() && !isLoadingMailContent),
        [isLoadingMailContent, loadGlobalData],
    );

    /**
     * Vérifier si les données du panneau de liste sont chargées et prêtes à être affichées
     */
    const loadListPanelData = React.useCallback(
        () => !!(loadGlobalData() && reminderPublisherFactureAchatsDataSource.status !== 'loading'),
        [loadGlobalData, reminderPublisherFactureAchatsDataSource.status],
    );

    const { loading: globaleLoading } = useLoadingStatus({ checkReady: loadGlobalData });
    const { loading: previewPanelLoading } = useLoadingStatus({ checkReady: loadPreviewPanelData });
    const { loading: listPanelLoading } = useLoadingStatus({ checkReady: loadListPanelData });

    return (
        <Grid
            container
            rowSpacing={0}
        >
            <Grid
                item
                lg={12}
            >
                <SkeletonLoader
                    isLoading={globaleLoading}
                    skeleton={<HeaderSkeleton />}
                >
                    <SupplierInfoCard
                        reminderPublisher={reminderPublisher}
                        fetchInvoices={fetchInvoices}
                        onRefreshMailContent={refreshMailContent}
                    />
                </SkeletonLoader>
            </Grid>

            {/* Colonne gauche : Commentaire personnalisé et prévisualisation du mail */}
            <Grid
                item
                lg={6}
                sm={12}
            >
                <SkeletonLoader
                    isLoading={previewPanelLoading}
                    skeleton={<EnhancedPanelSkeleton skeletonMode="enhanced" />}
                >
                    <MailPreview
                        mailContent={mailContent}
                        isLoading={isLoadingMailContent}
                        attachmentPage={attachmentPage}
                        attachmentCount={attachmentCount}
                        onAttachmentPageChange={setAttachmentPage}
                        reminderPublisher={reminderPublisher}
                    />
                </SkeletonLoader>
            </Grid>

            {/* Colonne droite : Informations fournisseur, liste des factures et boutons d'action */}
            <Grid
                item
                lg={6}
                sm={12}
            >
                <SkeletonLoader
                    isLoading={listPanelLoading}
                    skeleton={<EnhancedPanelSkeleton skeletonMode="simple" />}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'auto',
                            padding: '0',
                        }}
                    >
                        <ReminderHistory reminderPublisher={reminderPublisher} />
                        <InvoicesList
                            dataSource={reminderPublisherFactureAchatsDataSource}
                            fetchInvoices={fetchInvoices}
                            reminderPublisher={reminderPublisher}
                        />
                    </Box>
                </SkeletonLoader>
            </Grid>
        </Grid>
    );
};

export default PublisherReminderDetails;
